"use client"

import { Text } from "@/elements"
import clsx from "clsx"
import Link from "next/link"
import { usePathname } from "next/navigation"
import React, { useEffect, useRef } from "react"

interface Props {
  activeApplicationsCount: number | undefined
  activeInvitationsCount: number | undefined
}

export const JobsHeaderNav: React.FC<Props> = ({ activeApplicationsCount, activeInvitationsCount }) => {
  const pathname = usePathname()

  const hasActiveApplications = Boolean(activeApplicationsCount && activeApplicationsCount > 0)
  const hasActiveInvitations = Boolean(activeInvitationsCount && activeInvitationsCount > 0)

  const jobsBoardActive = pathname === "/jobs" || pathname === "/"

  const jobsBoardRef = useRef<HTMLDivElement>(null)
  const applicationsRef = useRef<HTMLDivElement>(null)
  const invitationsRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (jobsBoardActive && jobsBoardRef.current) {
      jobsBoardRef.current.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" })
    } else if (pathname === "/jobs/applications" && applicationsRef.current) {
      applicationsRef.current.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" })
    } else if (pathname === "/jobs/invitations" && invitationsRef.current) {
      invitationsRef.current.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" })
    }
  }, [pathname, jobsBoardActive])

  return (
    <div className="flex h-[47px] flex-row items-center gap-6 px-5 sm:gap-9 sm:px-0">
      <div ref={jobsBoardRef} id="jobs-board" className="group relative flex flex-shrink-0 flex-row pb-1">
        <Link href="/jobs">
          <Text variant="h5" className={clsx("hover:text-fg-1", jobsBoardActive ? "text-fg-1" : "text-fg-3")}>
            Job Board
          </Text>
        </Link>
        <div
          className={clsx(
            "absolute bottom-[-2.5px] right-0 w-full border-b border-fg-1 opacity-0 group-hover:opacity-100",
            jobsBoardActive && "opacity-100"
          )}
        />
      </div>
      <div
        ref={applicationsRef}
        id="applications"
        className="group relative flex flex-shrink-0 flex-row items-center pb-1">
        <Link href="/jobs/applications">
          <Text
            variant="h5"
            style={{
              fontVariantLigatures: "none",
            }}
            className={clsx("group-hover:text-fg-1", pathname === "/jobs/applications" ? "text-fg-1" : "text-fg-3")}>
            Applications
            {hasActiveApplications && (
              <Text
                element="span"
                variant="h5"
                className="ml-2 hidden sm:inline-block">{` \u0028${activeApplicationsCount}\u0029`}</Text>
            )}
          </Text>
        </Link>
        {hasActiveApplications && <div className="ml-2 hidden size-2 rounded-full bg-primary-3 sm:inline-block" />}
        <div
          className={clsx(
            "absolute bottom-[-2.5px] right-0 w-full border-b border-fg-1 opacity-0 group-hover:opacity-100",
            pathname === "/jobs/applications" && "opacity-100"
          )}
        />
      </div>
      <div ref={invitationsRef} id="invitations" className="pr-5 sm:pr-0">
        <div className="group relative flex flex-shrink-0 flex-row items-center pb-1">
          <Link href="/jobs/invitations">
            <Text
              variant="h5"
              style={{
                fontVariantLigatures: "none",
              }}
              className={clsx("group-hover:text-fg-1", pathname === "/jobs/invitations" ? "text-fg-1" : "text-fg-3")}>
              Invitations
              {hasActiveInvitations && (
                <Text
                  element="span"
                  variant="h5"
                  className="ml-2 hidden sm:inline-block">{` \u0028${activeInvitationsCount}\u0029`}</Text>
              )}
            </Text>
          </Link>
          {hasActiveInvitations && <div className="ml-2 size-2 rounded-full bg-primary-3" />}
          <div
            className={clsx(
              "absolute bottom-[-2.5px] right-0 w-full border-b border-fg-1 opacity-0 group-hover:opacity-100",
              pathname === "/jobs/invitations" && "opacity-100"
            )}
          />
        </div>
      </div>
    </div>
  )
}
